/**
 * Labstep
 *
 * @module prosemirror/utils
 * @desc ProseMirror generic utils
 */

import { allowedImageExtensions } from 'labstep-web/constants/mime-types';
import { Experiment } from 'labstep-web/models/experiment.model';
import type { ExperimentWorkflow } from 'labstep-web/models/experiment-workflow.model';
import { File } from 'labstep-web/models/file.model';
import { Group } from 'labstep-web/models/group.model';
import { Protocol } from 'labstep-web/models/protocol.model';
import { reactPropsKey } from 'labstep-web/prosemirror/extensions/external-comm';
import { createFiles } from 'labstep-web/prosemirror/extensions/paste';
import { sanitizationPluginKey } from 'labstep-web/prosemirror/extensions/sanitization/plugin';
import { navigatorService } from 'labstep-web/services/navigator.service';
import { getIdAttribute } from 'labstep-web/services/schema/helpers';
import { createEntity } from 'labstep-web/state/actions/entity';
import { uploadFile } from 'labstep-web/state/actions/file';
import { thumbnailFetchUrlAction } from 'labstep-web/state/actions/thumbnail';
import store from 'labstep-web/state/store';
import {
  Node,
  type NodeType,
  DOMParser as ProseMirrorDOMParser,
  type ResolvedPos,
  Slice,
} from 'prosemirror-model';
import type { EditorState, Transaction } from 'prosemirror-state';
import { Plugin, PluginKey } from 'prosemirror-state';
import { findWrapping } from 'prosemirror-transform';
import {
  Decoration,
  DecorationSet,
  type EditorView,
} from 'prosemirror-view';
import type { ElementsArrayType, ProseMirrorJSON } from './types';

export { sanitizeInitialState } from 'labstep-web/prosemirror/utils/sanitization';

/**
 * Plugin key for managing loading decorations during paste operations
 */
export const pasteLoadingPluginKey = new PluginKey('pasteLoading');

/**
 * Creates a plugin that manages loading decorations and editability during async paste operations
 */
export const createPasteLoadingPlugin = () => {
  return new Plugin({
    key: pasteLoadingPluginKey,
    state: {
      init() {
        return { decorations: DecorationSet.empty, isLoading: false };
      },
      apply(tr, state) {
        // Adjust decorations to changes in the document
        let decorations = state.decorations.map(tr.mapping, tr.doc);
        let isLoading = state.isLoading;

        // Check for meta to add or remove loading decoration
        const action = tr.getMeta(pasteLoadingPluginKey);
        if (action?.add) {
          const { pos } = action;
          const widget = Decoration.widget(
            pos,
            () => {
              const span = document.createElement('span');
              span.className = 'paste-loading-spinner';
              span.innerHTML = `
              <div class="ui active inline mini loader" style="margin: 0 8px;"></div>
              <span style="color: #999; font-size: 0.9em;">Pasting content...</span>
            `;
              return span;
            },
            { side: 1 },
          );
          decorations = decorations.add(tr.doc, [widget]);
          isLoading = true;
        } else if (action?.remove) {
          decorations = DecorationSet.empty;
          isLoading = false;
        }

        return { decorations, isLoading };
      },
    },
    props: {
      decorations(state) {
        const pluginState = this.getState(state);
        return pluginState?.decorations || DecorationSet.empty;
      },
      editable(state) {
        // Prevent edits while paste is loading
        const pluginState = this.getState(state);
        return !pluginState?.isLoading;
      },
    },
  });
};

const getExperimentIds = (
  experimentWorkflow: ExperimentWorkflow,
): number[] => experimentWorkflow.experiment_ids;

/**
 *
 * @desc Prevents propagation of certain key shortcuts
 * for example cmd+shift+r won't hard reload
 */
export const preventKeyPropagation = (
  e: KeyboardEvent,
  view: EditorView,
): void => {
  if (!view.hasFocus()) {
    return;
  }

  if (e.key === 'Tab') {
    e.stopPropagation();
    e.preventDefault();
  }

  if (e.ctrlKey || e.metaKey) {
    switch (String.fromCharCode(e.which).toLowerCase()) {
      case 'b':
      case 'i':
      case 'u':
      case ',':
      case '.':
      case 'd':
        e.stopPropagation();
        e.preventDefault();
        break;
      default:
        break;
    }
  }

  if ((e.ctrlKey || e.metaKey) && e.shiftKey) {
    switch (String.fromCharCode(e.which).toLowerCase()) {
      case 'l':
      case 'e':
      case 'r':
      case 'j':
        e.stopPropagation();
        e.preventDefault();
        break;
      default:
        break;
    }
  }
};

/**
 * Returns the equivalent text for MOD depending on the platform
 */
export const modToPlatformText = (): string =>
  `${navigatorService.isMac ? '⌘' : 'Ctr'}`;

/**
 * Configuration for different node types that support duplication
 */
type NodeDuplicationConfig = {
  nodeType: string;
  entityArrayKey?: keyof Experiment | keyof Protocol;
  idAttribute: 'id' | 'guid';
  useNumericId: boolean;
  duplicateAction: (
    sourceId: any,
    entity: Experiment | Protocol,
  ) => Promise<any>;
};

const NODE_DUPLICATION_CONFIGS: Record<
  string,
  NodeDuplicationConfig
> = {
  file: {
    nodeType: 'file',
    entityArrayKey: 'files' as keyof Experiment,
    idAttribute: 'id',
    useNumericId: true,
    duplicateAction: async (
      sourceId: any,
      entity: Experiment | Protocol,
    ) => {
      return await new Promise((resolve, reject) => {
        store.dispatch(
          uploadFile(
            {
              source_id: sourceId,
              entityType: entity.entityName,
              entityId: entity.id,
            },
            {
              onSuccess: ({ response }) => resolve(response),
              onFail: (error: any) => reject(error),
            },
          ),
        );
      });
    },
  },
  protocol_table: {
    nodeType: 'protocol_table',
    entityArrayKey: 'protocol_tables' as keyof Experiment,
    idAttribute: 'guid',
    useNumericId: false,
    duplicateAction: async (
      sourceId: any,
      entity: Experiment | Protocol,
    ) => {
      return await new Promise((resolve, reject) => {
        store.dispatch(
          createEntity(
            'protocol_table',
            { source_id: sourceId },
            entity.entityName,
            entity.id,
            undefined,
            {
              onSuccess: ({ response }: any) => resolve(response),
              onFail: (error: any) => reject(error),
            },
          ),
        );
      });
    },
  },
  protocol_step: {
    nodeType: 'protocol_step',
    entityArrayKey: 'protocol_steps' as keyof Experiment,
    idAttribute: 'guid',
    useNumericId: false,
    duplicateAction: async (
      sourceId: any,
      entity: Experiment | Protocol,
    ) => {
      // For protocol_step, we don't use source_id - just create a new step with the parent
      return await new Promise((resolve, reject) => {
        store.dispatch(
          createEntity(
            'protocol_step',
            {},
            entity.entityName,
            entity.id,
            undefined,
            {
              onSuccess: ({ response }: any) => resolve(response),
              onFail: (error: any) => reject(error),
            },
          ),
        );
      });
    },
  },
  protocol_timer: {
    nodeType: 'protocol_timer',
    entityArrayKey: 'protocol_timers' as keyof Experiment,
    idAttribute: 'guid',
    useNumericId: false,
    duplicateAction: async (
      sourceId: any,
      entity: Experiment | Protocol,
    ) => {
      return await new Promise((resolve, reject) => {
        store.dispatch(
          createEntity(
            'protocol_timer',
            { source_id: sourceId },
            entity.entityName,
            entity.id,
            undefined,
            {
              onSuccess: ({ response }: any) => resolve(response),
              onFail: (error: any) => reject(error),
            },
          ),
        );
      });
    },
  },
  protocol_value: {
    nodeType: 'protocol_value',
    entityArrayKey: 'protocol_values' as keyof Experiment,
    idAttribute: 'guid',
    useNumericId: false,
    duplicateAction: async (
      sourceId: any,
      entity: Experiment | Protocol,
    ) => {
      return await new Promise((resolve, reject) => {
        store.dispatch(
          createEntity(
            'protocol_value',
            { source_id: sourceId },
            entity.entityName,
            entity.id,
            undefined,
            {
              onSuccess: ({ response }: any) => resolve(response),
              onFail: (error: any) => reject(error),
            },
          ),
        );
      });
    },
  },
  metadata: {
    nodeType: 'metadata',
    entityArrayKey: 'metadatas' as keyof Experiment,
    idAttribute: 'id',
    useNumericId: true,
    duplicateAction: async (
      sourceId: any,
      entity: Experiment | Protocol,
    ) => {
      return await new Promise((resolve, reject) => {
        store.dispatch(
          createEntity(
            'metadata',
            { source_id: sourceId },
            'metadata_thread',
            entity.metadata_thread.id,
            undefined,
            {
              onSuccess: ({ response }: any) => resolve(response),
              onFail: (error: any) => reject(error),
            },
          ),
        );
      });
    },
  },
  molecule: {
    nodeType: 'molecule',
    entityArrayKey: 'molecules' as keyof Experiment,
    idAttribute: 'guid',
    useNumericId: false,
    duplicateAction: async (
      sourceId: any,
      entity: Experiment | Protocol,
    ) => {
      return await new Promise((resolve, reject) => {
        store.dispatch(
          createEntity(
            'molecule',
            { source_id: sourceId },
            entity.entityName,
            entity.id,
            undefined,
            {
              onSuccess: ({ response }: any) => resolve(response),
              onFail: (error: any) => reject(error),
            },
          ),
        );
      });
    },
  },
  jupyter_notebook: {
    nodeType: 'jupyter_notebook',
    entityArrayKey: 'jupyter_notebooks' as keyof Experiment,
    idAttribute: 'id',
    useNumericId: false,
    duplicateAction: async (
      sourceId: any,
      entity: Experiment | Protocol,
    ) => {
      return await new Promise((resolve, reject) => {
        store.dispatch(
          createEntity(
            'jupyter_notebook',
            { source_id: sourceId },
            entity.entityName,
            entity.id,
            undefined,
            {
              onSuccess: ({ response }: any) => resolve(response),
              onFail: (error: any) => reject(error),
            },
          ),
        );
      });
    },
  },
  smart_table: {
    nodeType: 'smart_table',
    idAttribute: 'id',
    useNumericId: false,
    duplicateAction: async (
      sourceId: any,
      entity: Experiment | Protocol,
    ) => {
      // Determine group_id based on entity type
      let groupId: number;
      if (entity instanceof Protocol) {
        // For Protocol, get the ProtocolCollection from Redux store and get owner.id
        // TODO: refactor so that entity_view is owned by protocol / experiment and group is not needed
        const state = store.getState();
        const protocolCollectionId =
          entity.permission_entity_info?.id;
        const protocolCollection =
          state.entities.protocol_collection?.byId?.[
            protocolCollectionId as number
          ];
        groupId = protocolCollection?.owner;
      } else {
        groupId = entity.experiment_workflow.owner.id;
      }

      return await new Promise((resolve, reject) => {
        store.dispatch(
          createEntity(
            'entity_view',
            { source_id: sourceId, group_id: groupId },
            Group.entityName,
            groupId,
            undefined,
            {
              onSuccess: ({ response }: any) => resolve(response),
              onFail: (error: any) => reject(error),
            },
          ),
        );
      });
    },
  },
};

/**
 * Generic function to duplicate foreign nodes that don't belong to the target entity
 * Returns a map of old IDs/GUIDs to new IDs/GUIDs
 */
export const duplicateForeignNodes = async (
  entity: Experiment | Protocol,
  content: ProseMirrorJSON[],
  nodeType: string,
): Promise<Map<string | number, string | number>> => {
  const config = NODE_DUPLICATION_CONFIGS[nodeType];
  if (!config) {
    throw new Error(
      `No duplication config found for node type: ${nodeType}`,
    );
  }

  const idMap = new Map<string | number, string | number>();

  // Get existing entity IDs/GUIDs (skip if entityArrayKey not defined)
  const existingIds: (string | number)[] = [];
  if (config.entityArrayKey) {
    const entityArray = (entity as any)[config.entityArrayKey] || [];
    existingIds.push(
      ...entityArray.map((item: any) =>
        config.useNumericId
          ? item.id
          : item[config.idAttribute] || item.guid,
      ),
    );
  }

  const foreignIds: (string | number)[] = [];

  // Recursively find all nodes of this type
  const findNodes = (nodes: ProseMirrorJSON[]) => {
    nodes.forEach((node) => {
      if (
        node.type === config.nodeType &&
        node.attrs?.[config.idAttribute]
      ) {
        const id = config.useNumericId
          ? Number(node.attrs[config.idAttribute])
          : String(node.attrs[config.idAttribute]);

        if (!existingIds.includes(id) && !foreignIds.includes(id)) {
          foreignIds.push(id);
        }
      }
      if (node.content) {
        findNodes(node.content);
      }
    });
  };

  findNodes(content);

  // Duplicate all foreign nodes in parallel
  const duplicationPromises = foreignIds.map(async (sourceId) => {
    try {
      const result: any = await config.duplicateAction(
        sourceId,
        entity,
      );

      if (result?.result) {
        // Handle both array results (e.g., files) and single ID results
        const newId = Array.isArray(result.result)
          ? result.result[0]
          : result.result;
        return { sourceId, newId };
      }
      return null;
    } catch (error) {
      console.error(
        `Failed to duplicate ${config.nodeType} ${sourceId}:`,
        error,
      );
      return null;
    }
  });

  const results = await Promise.all(duplicationPromises);

  results.forEach((result) => {
    if (result) {
      idMap.set(result.sourceId, result.newId);
    }
  });

  return idMap;
};

/**
 * Generic function to update node attributes based on an ID map
 */
export const updateNodeAttributes = (
  content: ProseMirrorJSON[],
  nodeType: string,
  idMap: Map<any, any>,
): ProseMirrorJSON[] => {
  const config = NODE_DUPLICATION_CONFIGS[nodeType];
  if (!config) {
    throw new Error(
      `No duplication config found for node type: ${nodeType}`,
    );
  }

  const updateNode = (node: ProseMirrorJSON): ProseMirrorJSON => {
    if (
      node.type === config.nodeType &&
      node.attrs?.[config.idAttribute]
    ) {
      const oldId = config.useNumericId
        ? Number(node.attrs[config.idAttribute])
        : String(node.attrs[config.idAttribute]);
      const newId = idMap.get(oldId);

      if (newId) {
        const updatedNode = {
          ...node,
          attrs: {
            ...node.attrs,
            [config.idAttribute]: newId,
          },
        };

        return updatedNode;
      }
    }

    if (node.content) {
      return { ...node, content: node.content.map(updateNode) };
    }

    return node;
  };

  return content.map(updateNode);
};

export const filterElementsFromContentJson = (
  ids: (string | number)[],
  content: any[],
  blockType: string,
  entityName: string,
  experimentWorkflow: null | ExperimentWorkflow,
): any => {
  return content.reduce((result, node) => {
    const nodeType =
      node.type === 'reference' ? node.attrs.entityName : node.type;
    if (nodeType === 'experiment_workflow_link') {
      if (
        experimentWorkflow &&
        experimentWorkflow.forward_links
          .map((e) => e.guid)
          .indexOf(node.attrs.guid) > -1
      ) {
        return [...result, node];
      }
      return result;
    }
    if (nodeType === 'experiment') {
      if (
        experimentWorkflow &&
        getExperimentIds(experimentWorkflow).indexOf(node.attrs.id) >
          -1
      ) {
        return [...result, node];
      }
      return result;
    }

    // We are not allowing experiment timer in experimentWorkflow atm
    if (
      experimentWorkflow &&
      (nodeType === 'experiment_timer' ||
        nodeType === 'protocol_timer')
    ) {
      return result;
    }

    if (nodeType !== blockType) {
      if (node.content) {
        return [
          ...result,
          {
            ...node,
            content: filterElementsFromContentJson(
              ids,
              node.content,
              blockType,
              entityName,
              experimentWorkflow,
            ),
          },
        ];
      }
      return [...result, node];
    }

    const idAttribute = getIdAttribute(nodeType);
    let id = node.attrs[idAttribute];
    id = Number(id) || id;
    // Should be in the provided ids to be valid
    if (ids.indexOf(id) > -1) {
      return [...result, node];
    }
    return result;
  }, []);
};

const getJsonWithCorrectSteps = (
  entity: Experiment | Protocol,
  slice: Slice,
  docNode: Node,
): {
  contentWithUnwrappedSteps: any[];
  openStart: number;
  openEnd: number;
} => {
  const contentJson = slice.content.toJSON() as ProseMirrorJSON[];
  let { openStart } = slice;
  let { openEnd } = slice;

  const stepsIdsOfEntity = entity.protocol_steps.map((e) => e.guid);
  const { content } = docNode.toJSON() as ProseMirrorJSON;
  const stepsIdsInDoc = content
    ? content
        .filter((n) => n.type.includes('step'))
        .map((n) => n.attrs?.guid)
    : [];

  const contentWithUnwrappedSteps = contentJson.reduce(
    (result, node, index) => {
      const guid = node.attrs?.guid as string | null;
      if (
        node.type.includes('step') &&
        (guid === null ||
          stepsIdsOfEntity.indexOf(guid) === -1 ||
          stepsIdsInDoc.indexOf(guid) > -1)
      ) {
        if (index === 0 && openStart > 0) {
          openStart -= 1;
        }
        if (index === contentJson.length - 1 && openEnd > 0) {
          openEnd -= 1;
        }
        return node.content ? [...result, ...node.content] : result;
      }
      return [...result, node];
    },
    [] as ProseMirrorJSON[],
  );
  return { contentWithUnwrappedSteps, openStart, openEnd };
};

export const getElementsArray = (
  entity: Experiment | Protocol,
): ElementsArrayType => {
  const metadataIds = entity.metadata_thread.metadatas.map(
    (m) => m.id,
  );
  const fileIds = entity.files.map((f) => f.id);
  const moleculeIds = entity.molecules.map((e) => e.guid);
  const protocolValueIds = entity.protocol_values.map((e) => e.guid);
  const protocolTimerIds = entity.protocol_timers.map((e) => e.guid);
  const protocolTableIds = entity.protocol_tables.map((e) => e.guid);
  const protocolDeviceIds = entity.protocol_devices.map(
    (e) => e.guid,
  );
  const protocolStepIds = entity.protocol_steps.map((e) => e.guid);
  const jupyterNotebookIds = entity.jupyter_notebooks.map(
    (e) => e.id,
  );

  const elementsArray: ElementsArrayType = [
    { key: 'metadata', ids: metadataIds },
    { key: 'file', ids: fileIds },
    { key: 'molecule', ids: moleculeIds },
    { key: 'protocol_value', ids: protocolValueIds },
    { key: 'protocol_timer', ids: protocolTimerIds },
    { key: 'protocol_table', ids: protocolTableIds },
    { key: 'protocol_device', ids: protocolDeviceIds },
    { key: 'protocol_step', ids: protocolStepIds },
    { key: 'jupyter_notebook', ids: jupyterNotebookIds },
  ];

  if (entity instanceof Experiment) {
    return [
      ...elementsArray,
      // legacy
      { key: 'experiment_value', ids: protocolValueIds },
      { key: 'experiment_timer', ids: protocolTimerIds },
      { key: 'experiment_table', ids: protocolTableIds },
      { key: 'experiment_step', ids: protocolStepIds },
    ];
  }

  return elementsArray;
};

export const getJsonWithoutForeignEntities = (
  entity: Experiment | Protocol,
  json: any[],
  experimentWorkflow: ExperimentWorkflow,
): any[] => {
  const elementsArray = getElementsArray(entity);
  return elementsArray.reduce(
    (result, elementArray) =>
      filterElementsFromContentJson(
        elementArray.ids,
        result,
        elementArray.key,
        entity.entityName,
        experimentWorkflow,
      ),
    json,
  );
};

/**
 *
 * @description Filters out nodes that don't correspond to
 * sub-entities of the entity
 */
export const getFilteredSlice = (
  entity: Experiment | Protocol,
  slice: Slice,
  view: EditorView,
  docNode: Node,
  experimentWorkflow: ExperimentWorkflow,
): Slice => {
  const { contentWithUnwrappedSteps, openStart, openEnd } =
    getJsonWithCorrectSteps(entity, slice, docNode);

  const finalJson = getJsonWithoutForeignEntities(
    entity,
    contentWithUnwrappedSteps,
    experimentWorkflow,
  );

  const node = Node.fromJSON(view.state.schema, {
    type: 'doc',
    content: finalJson,
  });
  const fragment = node.content;
  return new Slice(fragment, openStart, openEnd);
};

export const getTopLevelBlocksSelected = (
  state: EditorState,
): Node[] => {
  let blocks: any = [];
  state.doc.nodesBetween(
    state.selection.from,
    state.selection.to,
    (n) => {
      if (n.type.spec.group === 'block') {
        blocks = [...blocks, n];
        return false;
      }
      return true;
    },
  );
  return blocks;
};

export const isSingleBlockSelected = (
  state: EditorState,
): boolean => {
  return getTopLevelBlocksSelected(state).length < 2;
};

export const isSingleBlockSelectedAndAllowsContent = (
  state: EditorState,
): boolean => {
  if (state.selection.toJSON().type !== 'text') {
    return false;
  }
  const topLevelBlocksSelected = getTopLevelBlocksSelected(state);
  return (
    topLevelBlocksSelected.length === 1 &&
    !!topLevelBlocksSelected[0].type.spec.content
  );
};

export const isSingleTextSelected = (state: EditorState): boolean => {
  if (!isSingleBlockSelected(state)) {
    return false;
  }
  let nodes: Node[] = [];
  state.doc.nodesBetween(
    state.selection.from,
    state.selection.to,
    (n) => {
      if (n.type.spec.group !== 'block') {
        nodes = [...nodes, n];
        return false;
      }
      return true;
    },
  );
  return nodes.length === 1 && nodes[0].type.name === 'text';
};

/**
 *
 * @param state ProseMirror State
 * @returns selected text
 */
export const getSelectedText = (state: EditorState): string => {
  const {
    doc,
    selection: { from, to },
  } = state;
  const text = doc.textBetween(from, to);
  return text;
};

export const getSelectedTextForElement = (
  state: EditorState,
): string => {
  if (isSingleTextSelected(state)) {
    return getSelectedText(state);
  }
  return '';
};

export const canAddElement = (state: EditorState): boolean =>
  isSingleTextSelected(state) || state.selection.empty;

export const handlePaste = (
  view: EditorView,
  e: ClipboardEvent,
  slice: Slice,
): boolean => {
  const { entity, experimentWorkflow } = reactPropsKey.getState(
    view.state,
  );

  const { clipboardData } = e;
  if (!clipboardData) {
    return false;
  }

  // Check if we're pasting inside a table with HTML content (e.g., Excel)
  // If so, use default paste behavior to preserve table structure and ignore any files
  const { $from } = view.state.selection;
  const isInTable =
    $from.node(-1)?.type.spec.tableRole === 'row' ||
    $from.node(-2)?.type.spec.tableRole === 'table' ||
    $from.node(-3)?.type.spec.tableRole === 'table';

  let htmlContent = clipboardData.getData('text/html');

  // Excel includes both HTML and a screenshot file - prioritize HTML in tables
  if (isInTable && htmlContent && slice.content.size > 0) {
    return false;
  }

  const { files } = clipboardData;

  // Extract base64 images from HTML (e.g., from Word) and tag them for file upload
  const base64Images: File[] = [];
  const imageIndices: number[] = [];

  if (htmlContent) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    const imgElements = tempDiv.querySelectorAll(
      'img[src^="data:image"]',
    );

    imgElements.forEach((img, index) => {
      const src = img.getAttribute('src');
      if (!src || !src.startsWith('data:image')) {
        return;
      }

      try {
        // Extract image type and base64 data
        const matches = src.match(/data:image\/([^;]+);base64,(.+)/);
        if (!matches) {
          return;
        }

        const [, imageType, base64Data] = matches;

        // Convert base64 to blob
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
          type: `image/${imageType}`,
        });

        // Create a File object (using browser File constructor)
        const file = new (window as any).File(
          [blob],
          `pasted-image-${index}.${imageType}`,
          {
            type: `image/${imageType}`,
          },
        );
        base64Images.push(file);
        imageIndices.push(index);

        // Tag the img element with a placeholder attribute
        img.setAttribute('labstep-file-index', String(index));
        // Remove the base64 src to avoid bloating the HTML
        img.removeAttribute('src');
      } catch (error) {
        console.error('Failed to convert base64 image:', error);
      }
    });

    // Update HTML content with tagged images
    if (base64Images.length > 0) {
      htmlContent = tempDiv.innerHTML;
    }
  }

  // Use base64 images if available, otherwise use clipboard files
  // Don't combine them as Word often duplicates the last image in both formats
  const allFiles =
    base64Images.length > 0
      ? base64Images
      : files
        ? Array.from(files)
        : [];

  // https://github.com/Labstep/web/issues/5959
  // Need to check if files is set
  if (allFiles.length > 0) {
    const contentAsText = clipboardData.getData('Text');
    const isSupported = allowedImageExtensions.some((extension) =>
      contentAsText.endsWith(extension),
    );

    // If pasting only images (no text content), handle as file upload
    if ((isSupported || slice.content.size === 0) && !contentAsText) {
      e.preventDefault();
      createFiles(view.state, view.dispatch, entity, allFiles, view);
      return true;
    }

    // If pasting mixed content (text + images from Word/etc)
    if (slice.content.size > 0 && allFiles.length > 0) {
      e.preventDefault();

      // Upload files first, then parse HTML with file IDs
      const uploadedFilesMap = new Map<number, File>();
      let uploadedCount = 0;

      allFiles.forEach((file, index) => {
        store.dispatch(
          uploadFile(
            {
              file,
              entityType: entity.entityName,
              entityId: entity.id,
            },
            {
              onSuccess: ({ response }) => {
                const id = (response as any).result[0];
                const uploadedFile: File = (response as any).entities
                  .file[id];
                uploadedFilesMap.set(index, uploadedFile);
                uploadedCount++;

                // Once all files are uploaded, update HTML and paste
                if (uploadedCount === allFiles.length) {
                  // Update HTML with file IDs
                  const tempDiv = document.createElement('div');
                  tempDiv.innerHTML = htmlContent;
                  const taggedImages = tempDiv.querySelectorAll(
                    'img[labstep-file-index]',
                  );

                  taggedImages.forEach((img) => {
                    const fileIndex = Number.parseInt(
                      img.getAttribute('labstep-file-index') || '',
                    );
                    const uploadedFile =
                      uploadedFilesMap.get(fileIndex);
                    if (uploadedFile) {
                      img.setAttribute(
                        'labstep-file-id',
                        String(uploadedFile.id),
                      );
                      img.removeAttribute('labstep-file-index');
                    }
                  });

                  // Parse and insert the updated HTML using ProseMirror's parser
                  const updatedHtml = tempDiv.innerHTML;
                  const parser = new DOMParser();
                  const doc = parser.parseFromString(
                    updatedHtml,
                    'text/html',
                  );

                  // Use ProseMirror's DOMParser to convert HTML to a slice
                  const pmDOMParser = ProseMirrorDOMParser.fromSchema(
                    view.state.schema,
                  );
                  const parsedSlice = pmDOMParser.parseSlice(
                    doc.body,
                    {
                      preserveWhitespace: true,
                      context: view.state.selection.$from,
                    },
                  );

                  const tr =
                    view.state.tr.replaceSelection(parsedSlice);
                  view.dispatch(tr);

                  // Fetch thumbnails for images
                  uploadedFilesMap.forEach((file) => {
                    if (File.getIsImage(file.name, file.mime_type)) {
                      store.dispatch(
                        thumbnailFetchUrlAction(
                          file.guid,
                          { h: 1280, w: 1280 },
                          {},
                        ),
                      );
                    }
                  });
                }
              },
            },
          ),
        );
      });

      return true;
    }
  }

  if (
    slice.openEnd === slice.openStart &&
    // If table paste don't do filtering (taken from prosemirror-tables)
    slice.content.firstChild?.type.name.startsWith('table')
  ) {
    return false;
  }

  if (
    slice.content.size === 0 ||
    // If table paste don't do filtering (taken from prosemirror-tables)
    slice.content.firstChild?.type.spec.tableRole === 'table'
  ) {
    return false;
  }

  // Check if there are any foreign nodes and duplicate them
  const contentJson = slice.content.toJSON() as ProseMirrorJSON[];

  const checkForForeignNodes = (
    nodes: any[],
  ): Record<string, boolean> => {
    const foreignNodeTypes: Record<string, boolean> = {};

    const checkNode = (n: any) => {
      // Check all configured node types
      for (const [nodeType, config] of Object.entries(
        NODE_DUPLICATION_CONFIGS,
      )) {
        if (
          n.type === config.nodeType &&
          n.attrs?.[config.idAttribute]
        ) {
          const id = config.useNumericId
            ? Number(n.attrs[config.idAttribute])
            : String(n.attrs[config.idAttribute]);

          // Get existing IDs (skip if entityArrayKey not defined)
          const existingIds: (string | number)[] = [];
          if (config.entityArrayKey) {
            const entityArray =
              (entity as any)[config.entityArrayKey] || [];
            existingIds.push(
              ...entityArray.map((item: any) =>
                config.useNumericId ? item.id : item.guid,
              ),
            );
          }

          if (!existingIds.includes(id)) {
            foreignNodeTypes[nodeType] = true;
          }
        }
      }

      if (n.content) {
        n.content.forEach(checkNode);
      }
    };

    nodes.forEach(checkNode);
    return foreignNodeTypes;
  };

  const foreignNodeTypes = checkForForeignNodes(contentJson);
  const hasForeignNodes = Object.keys(foreignNodeTypes).length > 0;

  if (hasForeignNodes) {
    // Handle file and table duplication asynchronously
    e.preventDefault();

    // Show loading spinner at the paste position
    const pastePos = view.state.selection.from;
    const trLoading = view.state.tr.setMeta(pasteLoadingPluginKey, {
      add: true,
      pos: pastePos,
    });
    view.dispatch(trLoading);

    (async () => {
      try {
        // Duplicate foreign entities in parallel and collect ID maps
        const idMaps: Record<string, Map<any, any>> = {};

        const duplicationPromises = Object.keys(foreignNodeTypes).map(
          async (nodeType) => ({
            nodeType,
            idMap: await duplicateForeignNodes(
              entity,
              contentJson,
              nodeType,
            ),
          }),
        );

        const results = await Promise.all(duplicationPromises);

        results.forEach(({ nodeType, idMap }) => {
          idMaps[nodeType] = idMap;
        });

        // Update IDs in the content
        let updatedContentJson = contentJson;
        for (const [nodeType, idMap] of Object.entries(idMaps)) {
          updatedContentJson = updateNodeAttributes(
            updatedContentJson,
            nodeType,
            idMap,
          );
        }

        // Get the state from the store to access newly created entities
        const state = store.getState();

        // Collect all new entities
        const newEntitiesByType: Record<string, any[]> = {};

        for (const [nodeType, idMap] of Object.entries(idMaps)) {
          const config = NODE_DUPLICATION_CONFIGS[nodeType];
          // Skip entity collection if entityArrayKey is not defined
          if (!config.entityArrayKey) {
            continue;
          }
          const newIds = Array.from(idMap.values());
          const entityKey = config.entityArrayKey as string;
          const stateKey = nodeType as keyof typeof state.entities;

          newEntitiesByType[entityKey] = newIds
            .map(
              (id: any) =>
                (state.entities[stateKey] as any)?.byId?.[id],
            )
            .filter(Boolean);
        }

        // Create a temporary entity with updated entities lists
        const entityWithNewEntities = {
          ...entity,
        };

        for (const [key, newEntities] of Object.entries(
          newEntitiesByType,
        )) {
          // Special handling for metadata - need to update metadata_thread.metadatas
          if (key === 'metadatas') {
            (entityWithNewEntities as any).metadata_thread = {
              ...entity.metadata_thread,
              metadatas: [
                ...entity.metadata_thread.metadatas,
                ...newEntities,
              ],
            };
          } else {
            (entityWithNewEntities as any)[key] = [
              ...(entity as any)[key],
              ...newEntities,
            ];
          }
        }

        // Create a new slice with updated IDs
        const updatedNode = Node.fromJSON(view.state.schema, {
          type: 'doc',
          content: updatedContentJson,
        });
        const updatedSlice = new Slice(
          updatedNode.content,
          slice.openStart,
          slice.openEnd,
        );

        // Now filter the slice with updated IDs
        const filteredSlice = getFilteredSlice(
          entityWithNewEntities,
          updatedSlice,
          view,
          view.state.doc,
          experimentWorkflow,
        );

        const tr = view.state.tr.replaceSelection(filteredSlice);
        tr.setMeta(sanitizationPluginKey, {});
        // Remove loading spinner
        tr.setMeta(pasteLoadingPluginKey, { remove: true });

        view.dispatch(tr);
      } catch (error) {
        console.error(
          'Failed to duplicate entities during paste:',
          error,
        );
        // Fall back to regular paste without duplication
        const filteredSlice = getFilteredSlice(
          entity,
          slice,
          view,
          view.state.doc,
          experimentWorkflow,
        );
        const tr = view.state.tr.replaceSelection(filteredSlice);
        tr.setMeta(sanitizationPluginKey, {});
        // Remove loading spinner even on error
        tr.setMeta(pasteLoadingPluginKey, { remove: true });
        view.dispatch(tr);
      }
    })();

    return true;
  }

  const filteredSlice = getFilteredSlice(
    entity,
    slice,
    view,
    view.state.doc,
    experimentWorkflow,
  );
  const tr = view.state.tr.replaceSelection(filteredSlice);
  tr.setMeta(sanitizationPluginKey, {});

  view.dispatch(tr);

  return true;
};

export const isWrappingPossible = (
  nodeType: NodeType,
  state: EditorState,
): boolean => {
  const { $from, $to } = state.selection;
  const range = $from.blockRange($to);
  if (!range) {
    return false;
  }

  const wrap = findWrapping(range, nodeType);
  if (!wrap) {
    return false;
  }

  return true;
};

export const wrapIn = (
  nodeType: NodeType,
  attrs: Record<string, unknown>,
  tr: Transaction,
  $from: ResolvedPos,
  $to: ResolvedPos,
): Transaction => {
  const range = $from.blockRange($to);
  const wrapping = range && findWrapping(range, nodeType, attrs);
  if (wrapping) {
    // FIXME no-param-reassign
    tr = tr.wrap(range, wrapping);
  }
  return tr;
};

/**
 * Returns true if cursor is at a blank line
 *
 */
export const isCursorAtBlankLine = (state: EditorState): boolean => {
  const {
    doc,
    selection: { head, empty },
  } = state;
  return empty && doc.resolve(head).parent.content.size === 0;
};

export const isCursorInTable = (state: EditorState): boolean => {
  const {
    doc,
    selection: { head },
  } = state;
  const node = doc.resolve(head);
  const grandparent = node.node(node.depth - 1);
  return grandparent && grandparent.type.name === 'table_cell';
};

/**
 *
 * @description Returns the plugin state
 * @param state
 * @returns object
 */
export const getPluginState = (
  state: EditorState,
  key: PluginKey,
): any => {
  return state.plugins
    .find((p) => p.spec.key === key)
    ?.getState(state);
};

export function updateFileDecorations(
  view: EditorView,
  editable: boolean,
) {
  const decorations: Decoration[] = [];

  view.state.doc.descendants((node, pos) => {
    if (node.type.name === 'file') {
      decorations.push(
        Decoration.node(pos, pos + node.nodeSize, {
          class: `editable-${String(editable)}`,
        }),
      );
    }
  });

  const newDecoSet = DecorationSet.create(
    view.state.doc,
    decorations,
  );

  // Merge with existing decorations (if any)
  const existingDecorations = (view.props.decorations?.(view.state) ||
    DecorationSet.empty) as DecorationSet;
  const mergedDecorations = DecorationSet.create(view.state.doc, [
    ...existingDecorations.find(),
    ...newDecoSet.find(),
  ]);

  // Update the view with the merged decorations
  view.setProps({
    decorations: () => mergedDecorations,
  });
}
